import { useState } from "react";
import {
  ACCOUNT_FEEDBACK_THANKS_COPY,
  FEEDBACK_MESSAGE_MAX_CHARS,
} from "@panelgenie/contracts";
import {
  Banner,
  Button,
  Card,
  Heading,
  Label,
  Textarea,
} from "@panelgenie/shared-ui";

/**
 * In-product feedback affordance. The user taps "Send feedback", a
 * `<textarea>` opens inline, the parent owns the tRPC call and
 * threads the session id + route + tier into the payload.
 *
 * State machine mirrors `AccountExportState` / `AccountDeleteState` so
 * the parent can drive a spinner / error / thanks confirmation
 * through `state` alone.
 */
export type FeedbackPanelState =
  | { readonly kind: "idle" }
  | { readonly kind: "submitting" }
  | { readonly kind: "submitted" }
  | { readonly kind: "error"; readonly message: string };

export type FeedbackPanelProps = {
  readonly state: FeedbackPanelState;
  readonly onSubmit: (message: string) => void;
};

export function FeedbackPanel({ state, onSubmit }: FeedbackPanelProps) {
  const [message, setMessage] = useState("");
  const trimmed = message.trim();
  const isBusy = state.kind === "submitting";
  const isOverCap = trimmed.length > FEEDBACK_MESSAGE_MAX_CHARS;
  const canSubmit = trimmed.length > 0 && !isOverCap && !isBusy;

  // When the parent reports `submitted`, reset the local textarea so a
  // second submission starts from an empty slate.
  if (state.kind === "submitted" && message !== "") {
    setMessage("");
  }

  return (
    <Card aria-label="account-feedback">
      <Card.Header>
        <Heading level="h2">Send feedback</Heading>
      </Card.Header>
      <Card.Body className="space-y-3">
        <p className="text-base text-fg">
          Tell us what worked, what didn&apos;t, or what&apos;s missing.
          Your note arrives alongside the session id of your current
          visit so we can correlate against any crashes we saw — no need
          to mention them yourself.
        </p>
        <div className="space-y-1">
          <Label htmlFor="feedback-message-input">Your message</Label>
          <Textarea
            id="feedback-message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            aria-label="feedback-message"
            rows={5}
            maxLength={FEEDBACK_MESSAGE_MAX_CHARS + 1}
            disabled={isBusy}
            placeholder="What's on your mind?"
            invalid={isOverCap}
          />
          <p
            aria-label="feedback-char-counter"
            className={
              isOverCap ? "text-xs text-danger" : "text-xs text-mute"
            }
          >
            {trimmed.length} / {FEEDBACK_MESSAGE_MAX_CHARS} characters
            {isOverCap ? " — over the limit, please shorten" : ""}
          </p>
        </div>
        <Button
          onClick={() => onSubmit(trimmed)}
          disabled={!canSubmit}
          loading={isBusy}
          aria-label="send-feedback"
        >
          {isBusy ? "Sending…" : "Send feedback"}
        </Button>

        {state.kind === "error" ? (
          <Banner variant="danger" role="alert">
            {state.message}
          </Banner>
        ) : null}

        {state.kind === "submitted" ? (
          <Banner
            variant="success"
            role="status"
            aria-label="feedback-thanks"
          >
            {ACCOUNT_FEEDBACK_THANKS_COPY}
          </Banner>
        ) : null}
      </Card.Body>
    </Card>
  );
}
